import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { clamp, roundTo } from '../types/geometry.js';
import { analyzeLightingBalance } from '../quality/lightingBalance.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

const MIN_FACE_PIXEL_WIDTH = 24;
const MIN_HALF_SAMPLES = 80;

/**
 * «Равномерность освещения».
 *
 * Splits the face at the measured `faceCenterX` and compares the two halves,
 * plus a top-to-bottom falloff across the face box. This is deliberately its own
 * id: the older `lighting` check keeps its own `lighting_dark_side` marker and
 * its backlight logic, while this one reports the balance numbers explicitly
 * (`leftFaceLuma`, `rightFaceLuma`, gradients) for the details view.
 *
 * Never a hard failure — uneven light is corrected by re-shooting.
 */
export const lightingBalanceCheck: ValidationCheck = {
  id: 'lighting_balance',
  title: 'Равномерность освещения',
  description:
    'Сравнивает яркость левой и правой половины лица и вертикальный перепад яркости по лицу.',
  kind: 'quality',
  requires: ['pixels', 'geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['pixels', 'geometry']);
    if (missing) return notChecked(ctx, lightingBalanceCheck, missing, ctx.rule.severity);
    const pixels = ctx.pixels;
    const geometry = ctx.geometry;
    if (!pixels || !geometry) {
      return notChecked(
        ctx,
        lightingBalanceCheck,
        'Недостаточно данных для оценки равномерности освещения.',
        ctx.rule.severity,
      );
    }

    const maxLumaDifference = numberParam(ctx.rule.params, 'maxLumaDifference', 28);
    /**
     * The percentage bound may be written either as a ratio (0,18 = 18 %) or as
     * a percentage number (18). Normalize so both profile styles behave.
     */
    const rawDifferencePercent = numberParam(
      ctx.rule.params,
      'maxLumaDifferencePercent',
      0.25,
    );
    const maxDifferenceRatio =
      rawDifferencePercent > 1 ? rawDifferencePercent / 100 : rawDifferencePercent;
    const maxVerticalGradient = numberParam(ctx.rule.params, 'maxVerticalGradient', 40);
    const uncertainBelowConfidence = numberParam(ctx.rule.params, 'uncertainBelowConfidence', 0.35);

    const analysis = analyzeLightingBalance({ buffer: pixels, geometry });
    const facePixelWidth = geometry.faceBox.width * pixels.width;
    const samples = Math.min(analysis.leftSamples, analysis.rightSamples);
    const sizeScore = clamp(samples / 400, 0, 1);
    const confidence = clamp(
      0.3 + geometry.faceBoxConfidence.value * 0.5 + sizeScore * 0.2,
      0,
      1,
    );
    const tooSmall =
      facePixelWidth < MIN_FACE_PIXEL_WIDTH ||
      samples < MIN_HALF_SAMPLES ||
      confidence < uncertainBelowConfidence;

    const unbalanced =
      analysis.difference > maxLumaDifference ||
      analysis.differencePercent / 100 > maxDifferenceRatio;
    const tooGradient = Math.abs(analysis.verticalGradient) > maxVerticalGradient;

    const status: CheckResult['status'] = tooSmall
      ? 'uncertain'
      : unbalanced
        ? 'warning'
        : tooGradient
          ? 'warning'
          : 'pass';

    const darkerRect = analysis.darkerSide === 'left' ? analysis.leftRect : analysis.rightRect;
    const problems: string[] = [];
    if (unbalanced) {
      problems.push(
        `разница между половинами лица ${roundTo(analysis.difference, 0)} (${analysis.darkerSide === 'left' ? 'левая' : 'правая'} сторона темнее)`,
      );
    }
    if (tooGradient) {
      problems.push(
        `перепад яркости сверху вниз ${roundTo(Math.abs(analysis.verticalGradient), 0)}`,
      );
    }

    return plainResult({
      id: lightingBalanceCheck.id,
      title: lightingBalanceCheck.title,
      status,
      severity: ctx.rule.severity,
      value: roundTo(analysis.difference, 1),
      max: maxLumaDifference,
      message:
        status === 'pass'
          ? '✅ Освещение равномерное'
          : status === 'warning'
            ? '⚠ Одна сторона лица значительно темнее другой'
            : '⚠ Лицо на снимке слишком мелкое, чтобы надёжно оценить равномерность освещения.',
      requirement: `Разница яркости половин лица не более ${maxLumaDifference}; вертикальный перепад не более ${maxVerticalGradient}.`,
      fixability: 'retake',
      geometry: [
        {
          type: 'rect',
          coordinates: [
            roundTo(analysis.leftRect.x, 4),
            roundTo(analysis.leftRect.y, 4),
            roundTo(analysis.leftRect.width, 4),
            roundTo(analysis.leftRect.height, 4),
          ],
        },
        {
          type: 'rect',
          coordinates: [
            roundTo(analysis.rightRect.x, 4),
            roundTo(analysis.rightRect.y, 4),
            roundTo(analysis.rightRect.width, 4),
            roundTo(analysis.rightRect.height, 4),
          ],
        },
      ],
      problemRegions:
        status === 'warning'
          ? [
              {
                id: 'lighting_balance_dark_side',
                rect: {
                  x: roundTo(darkerRect.x, 4),
                  y: roundTo(darkerRect.y, 4),
                  width: roundTo(darkerRect.width, 4),
                  height: roundTo(darkerRect.height, 4),
                },
                message: 'Эта половина лица темнее другой — свет падает с одной стороны.',
                severity: 'warning',
              },
            ]
          : [],
      confidence: roundTo(confidence, 2),
      details: {
        leftFaceLuma: roundTo(analysis.leftFaceLuma, 1),
        rightFaceLuma: roundTo(analysis.rightFaceLuma, 1),
        difference: roundTo(analysis.difference, 1),
        differencePercent: roundTo(analysis.differencePercent, 1),
        verticalGradient: roundTo(analysis.verticalGradient, 1),
        horizontalGradient: roundTo(analysis.horizontalGradient, 1),
        topFaceLuma: roundTo(analysis.topFaceLuma, 1),
        bottomFaceLuma: roundTo(analysis.bottomFaceLuma, 1),
        darkerSide: analysis.darkerSide,
        problems,
        limits: {
          maxLumaDifference,
          maxLumaDifferencePercent: roundTo(maxDifferenceRatio * 100, 1),
          maxVerticalGradient,
        },
        displayValue: `${roundTo(analysis.difference, 0).toFixed(0)}`,
      },
    });
  },
};
